import React, { Suspense, useEffect, useRef, useState } from 'react'
import { BrowserRouter as Router, Route, Routes, Navigate, useParams, useLocation } from 'react-router-dom'
import { bool } from 'prop-types'

import { getCookie, setCookie } from 'cookie'
import PaidRoute from './Router.paidRoute'
import PermissionRoute from './Router.permissionRoute'
import { useFeatureFlag, FEATURE_FLAGS } from 'modules/featureFlags'
import AdminRoute from './Router.adminRoute'
import AdminMimicRoute from './Router.adminMimicRoute'

import Pipeline from 'applications/Pipeline'
import StaffAdminRedirect from './StaffAdminRedirect'

import Subscribers from 'applications/Subscribers'
import EmailCampaigns from 'applications/EmailCampaigns'
import Home from 'applications/Home'
import SignupMultistep from 'applications/SignupMultistep'
import Dashboard from 'applications/Dashboard'
import Traffic from 'applications/Traffic'
import Integrations from 'applications/Integrations'
import AI from 'applications/AI'
import TaskLibrary from 'applications/TaskLibrary'
import PreBuiltTaskDetail from 'applications/TaskLibrary/PreBuiltTaskDetail'
import MyTaskDetail from 'applications/TaskLibrary/MyTaskDetail'
import Settings from 'applications/Settings'
import Profile from 'applications/Profile'
import Plan from 'applications/Plan'
import Channels from 'applications/Channels'
import Action from 'applications/Action'
import FAQ from 'applications/FAQ'
import FAQBigcommerce from 'applications/FAQBigcommerce'
import Reports from 'applications/Reports'
import CustomReports from 'applications/CustomReports'
import Shipping from 'applications/Shipping'
import Claims from 'applications/Claims'
import Inventory from 'applications/Inventory'
import Unauthorized from 'applications/Unauthorized'
import SiteToolsLanding from 'sharedComponents/SiteToolsLanding'
import FilePickerPopup from 'sharedComponents/SearchBox/FilePickerPopup'
import ImportOrdersPopup from 'applications/Integrations/components/ImportOrdersPopup'

import IntercomCurl from 'sharedComponents/IntercomCurl'
import MarketingBanner from 'sharedComponents/MarketingBanner'
import MfaSetupNudgeBanner from 'sharedComponents/MfaSetupNudgeBanner'
import ErrorBoundary from 'sharedComponents/ErrorBoundary'
import Footer from 'sharedComponents/Footer'
import ActionModal from './ActionModal'
import SidebarLayout from 'sharedComponents/SidebarLayout'

import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import ReactTooltip from 'react-tooltip'

import {
  teamPageAccessAllowed,
  teamShippingCapabilityAllowed,
  SHIPPING_CAP_KEYS,
} from 'applications/Settings/views/SharedAccounts/teamPagePermissions'

// Wrapper components for routes that need params
const ReportsWithParams = ({ accessPermissions, accessParams }) => {
  const { id, reportName } = useParams()
  if (!teamPageAccessAllowed(accessPermissions, 'reports')) {
    return <Unauthorized page="Reports" />
  }
  return <Reports applyReport={!!id} reportId={id} reportName={reportName} {...accessParams} />
}

const CustomReportsWithParams = ({ accessPermissions, accessParams }) => {
  const { id } = useParams()
  if (!teamPageAccessAllowed(accessPermissions, 'reports')) {
    return <Unauthorized page="Reports" />
  }
  return <CustomReports applyCustomReport={!!id} customReportId={id} {...accessParams} />
}

/** Redirect /pipeline/* to /admin/pipeline/*, preserving sub-path and query. */
const PipelineRedirect = () => {
  const loc = useLocation()
  return <Navigate to={loc.pathname.replace('/pipeline', '/admin/pipeline') + loc.search} replace />
}

const defaultRedirect = ({ route, hasAccounts, onboardSkipped, hasOrgWorkspace, requiresOnboarding, siteToolsOnly }) => {
  const is_sign_up = getCookie('is_sign_up')
  const linking_account = getCookie('linking_account'),
    linking_fulfilled = getCookie('linking_fulfilled')
  const confirming_charge = getCookie('confirming_charge'),
    charge_fulfilled = getCookie('charge_fulfilled')
  const slack_fulfilled = getCookie('slack_fulfilled')

  const needsOnboarding =
    requiresOnboarding === true
      ? true
      : requiresOnboarding === false
        ? false
        : getCookie('onboarding_completed')
          ? // #693: completed client-side — don't fall into the derived branch,
            // which is always true for a no-store account (no accounts, and
            // onboardSkipped resets on reload).
            false
          : (!hasAccounts || is_sign_up) && !onboardSkipped && !hasOrgWorkspace

  if (needsOnboarding) {
    return '/onboard'
  } else if (linking_account || linking_fulfilled || slack_fulfilled) {
    return '/integrations'
  } else if (confirming_charge || charge_fulfilled) {
    return '/plan'
  } else if (siteToolsOnly) {
    // No connected store → land on /dashboard, which renders the SiteToolsLanding
    // "connect your tools" home (Open AI Assistant is the CTA there). Keeping this
    // consistent with finish() so a fresh login lands on the same connect-first home.
    return '/dashboard'
  } else if (route) {
    return route
  } else {
    return '/dashboard'
  }
}

// Pages the Free tier can always reach. "Lite" is just a Free-plan variant
// (planConfig marks it `product: 'Free', lite: true`), so it is treated
// IDENTICALLY to Free — there is no separate, more-restricted lite allow-list.
// Shipping is part of the free baseline: actual shipping access is governed
// downstream by the SHIPPING feature flag + RBAC capabilities (see
// `shippingFeatureEnabled`), not the plan tier, so neither Free nor Lite is
// plan-gated out of it.
// AI Chat is also free-reachable: papaya gates on credit balance
// (`requireAiCredits`), not plan tier, so Free/Lite with a one-time balance
// must not see the plan upgrade modal (#941).
const FREE_ALLOWED_PAGES = ['dashboard', 'plan', 'settings', 'profile', 'integrations', 'faq', 'action', 'channels', 'onboard', 'ai', 'shipping']
// Free-tier pages that render in full (no upgrade modal) once reached.
const FREE_FULL_ACCESS_PAGES = ['shipping', 'ai']

const AppRouter = ({
  hasAccounts,
  hasOrgWorkspace,
  requiresOnboarding,
  storeMarketplaceAccountCount,
  siteToolsOnly,
  noConnectedStore,
  hasBigcommerce,
  subscription,
  addOns,
  subscriptionActive,
  upgradeDismissed,
  applicationId,
  mimicStatus,
  reportTimeCost,
  adminFetching,
  accessPermissions,
  loginFromChannel,
  onboardSkipped,
  setOnboardSkipped,
  isLite,
  admin,
}) => {
  // Release master switch (layer 1). RBAC capability checks remain layer 2.
  const shippingFeatureEnabled = useFeatureFlag(FEATURE_FLAGS.SHIPPING)
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false)
  const mountTimeRef = useRef(performance.now())
  const [loadTimeMs, setLoadTimeMs] = useState<number | null>(null)

  useEffect(() => {
    if (requiresOnboarding === true) {
      setCookie('onboarding_completed', '')
    }
  }, [requiresOnboarding])

  // Check if this is the file picker popup page (rendered separately without app chrome)
  const isFilePickerPopup = window.location.pathname === '/file-picker-popup'

  if (isFilePickerPopup) {
    return (
      <Router future={{ v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/file-picker-popup" element={<FilePickerPopup />} />
        </Routes>
      </Router>
    )
  }

  // Standalone import-orders popup — opened by the MCP `start_ebay_order_import`
  // tool. Rendered without app chrome so the user gets a focused upload window
  // that can close itself when done.
  const isImportOrdersPopup = window.location.pathname === '/import-orders/ebay-popup'

  if (isImportOrdersPopup) {
    return (
      <Router future={{ v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/import-orders/ebay-popup" element={<ImportOrdersPopup />} />
        </Routes>
      </Router>
    )
  }
  const subscriptionInactiveOrUpgradeRequired = !subscriptionActive
  const normalizedApplicationId = (applicationId || '').toLowerCase()
  // Prefer the URL path for plan gating. Redux `applicationId` can desync from the
  // path (WooCommerce iframe login calls changeApplication without rewriting the
  // URL), which incorrectly free-gates /dashboard and auto-opens the upgrade modal
  // inside the WordPress plugin while app.payhelm.com works fine.
  const pathApplicationId = (
    typeof window !== 'undefined'
      ? (window?.location?.pathname || '').split('/').filter(Boolean)[0]
      : ''
  )?.toLowerCase() || ''
  // Task Library lives under /task-library but AppSidebar keeps Redux id `ai`
  // (changeApplication → navigate). Map it so Free still gets AI allow-list /
  // full-access treatment after path-based gating.
  const rawGateApplicationId = pathApplicationId || normalizedApplicationId
  const gateApplicationId =
    rawGateApplicationId === 'task-library' ? 'ai' : rawGateApplicationId
  const currentProduct = subscription?.plans?.[0]?.product
  const isFreeUser = currentProduct === 'Free' && !admin && subscription?.status !== 'trialing'
  // Lite users follow the exact same Free-tier rules (lite === free); the
  // separate lite allow-list is gone so a lite account can never be blocked
  // where a free account would not be.
  const freeAllowedPages = FREE_ALLOWED_PAGES
  const hasFreeFullAccess = FREE_FULL_ACCESS_PAGES.includes(gateApplicationId)
  const isFreeGated = isFreeUser && !freeAllowedPages.includes(gateApplicationId)

  // Mid-tier gating: features that require Business or higher plan (or an active add-on)
  const businessPlusPlanProducts = ['Business', 'Enterprise']
  const isBusinessPlus = businessPlusPlanProducts.includes(currentProduct)
  const hasActiveAddOn = (product) => (addOns || []).some(a => a?.plans?.[0]?.product === product)
  // Tabs that require Business plan OR an active add-on subscription
  const businessGatedTabs = { traffic: 'Traffic', inventory: 'Inventory' }
  const isBusinessGated = !admin && !isBusinessPlus && subscription?.status !== 'trialing' &&
    businessGatedTabs[gateApplicationId] && !hasActiveAddOn(businessGatedTabs[gateApplicationId])
  const allowContinue = (
    ['dashboard', 'shipping'].includes(gateApplicationId) && (admin || !subscriptionInactiveOrUpgradeRequired || isLite)
  ) || gateApplicationId === 'plan'
  const adminUpgradeDismissed = !!mimicStatus
  const limitedAccess = (isLite || isFreeUser || subscriptionInactiveOrUpgradeRequired) && !hasFreeFullAccess && !adminUpgradeDismissed && !admin && (subscription?.status !== 'trialing')
  const checkAccess = () => {
    if (adminUpgradeDismissed) {
      return true
    }
    if (limitedAccess) {
      setUpgradeModalOpen(true)
      return false
    }
    return true
  }
  const accessParams = { limitedAccess, checkAccess, upgradeDismissed }
  const aiAllowed = teamPageAccessAllowed(accessPermissions, 'ai')
  // Users with no connected store have no order data on Traffic/Reports/etc., so
  // those routes still render the site-tools landing. /dashboard always mounts the
  // real Dashboard after onboard (seeded custom tabs + in-dashboard connect CTAs).
  const showSiteTools = siteToolsOnly || (noConnectedStore && !hasOrgWorkspace)
  const siteToolsGuard = (element) => (showSiteTools ? <SiteToolsLanding aiAllowed={aiAllowed} /> : element)
  const gatingBlocksRoutes = (isFreeGated || isBusinessGated) && !adminUpgradeDismissed
  const setupComplete =
    requiresOnboarding === true
      ? false
      : (
        requiresOnboarding === false ||
        hasOrgWorkspace ||
        !!getCookie('onboarding_completed') ||
        (requiresOnboarding == null && (onboardSkipped || hasAccounts))
      )
  // #693: store connection is no longer required to enter the app. A user who
  // finished onboarding (or has any linked account / org workspace) reaches the
  // primary routes even with zero connected stores; non-dashboard analytics pages
  // fall back to SiteToolsLanding via siteToolsGuard.
  // NOTE: `requiresOnboarding === false` / `showSiteTools` must be in the second
  // clause — otherwise a completed no-store user (no accounts, and onboardSkipped
  // resets to false on reload) fails this gate and gets bounced to /onboard.
  const canRenderPrimaryRoutes = admin || (
    setupComplete &&
    (onboardSkipped || hasAccounts || hasOrgWorkspace || requiresOnboarding === false || showSiteTools) &&
    (!subscriptionInactiveOrUpgradeRequired || adminUpgradeDismissed || isLite)
  )

  // Routes content - shared between sidebar and navbar layouts
  const routesContent = (
    <Suspense fallback={<div style={{ minHeight: 'calc(100vh - 200px)' }} />}>{
      gatingBlocksRoutes ? (
        <div style={{ minHeight: 'calc(100vh - 200px)' }} />
      ) : canRenderPrimaryRoutes ? (
        <Routes>
          <Route path="/channels/*" element={<Channels />} />
          <Route path="/onboard/*" element={<SignupMultistep storeMarketplaceAccountCount={storeMarketplaceAccountCount} setOnboardSkipped={setOnboardSkipped} loginFromChannel={loginFromChannel}/>} />
          <Route
            path="/dashboard/*"
            element={
              teamPageAccessAllowed(accessPermissions, 'dashboard') ? (
                <Dashboard {...accessParams}/>
              ) : (
                <Unauthorized page="Dashboard" />
              )
            }
          />
          <Route
            path="/traffic/*"
            element={siteToolsGuard(
              teamPageAccessAllowed(accessPermissions, 'traffic') ? (
                <Traffic />
              ) : (
                <Unauthorized page="Traffic" />
              )
            )}
          />
          <Route path="/reports" element={siteToolsGuard(<ReportsWithParams accessPermissions={accessPermissions} accessParams={accessParams} />)} />
          <Route path="/reports/:id" element={siteToolsGuard(<ReportsWithParams accessPermissions={accessPermissions} accessParams={accessParams} />)} />
          <Route path="/reports/:id/:reportName" element={siteToolsGuard(<ReportsWithParams accessPermissions={accessPermissions} accessParams={accessParams} />)} />
          <Route
            path="/integrations/*"
            element={
              teamPageAccessAllowed(accessPermissions, 'integrations') ? (
                <Integrations />
              ) : (
                <Unauthorized page="Integrations" />
              )
            }
          />
          <Route path="/subscribers/*" element={<Subscribers />} />
          <Route path="/emailcampaigns/*" element={<EmailCampaigns {...accessParams} />} />
          <Route
            path="/ai/*"
            element={
              teamPageAccessAllowed(accessPermissions, 'ai') ? (
                <AI {...accessParams}/>
              ) : (
                <Unauthorized page="AI Chat" />
              )
            }
          />
          <Route
            path="/task-library"
            element={
              teamPageAccessAllowed(accessPermissions, 'ai') ? (
                <TaskLibrary admin={admin} {...accessParams} />
              ) : (
                <Unauthorized page="Task Library" />
              )
            }
          />
          <Route
            path="/task-library/pre-built/:slug"
            element={
              teamPageAccessAllowed(accessPermissions, 'ai') ? (
                <PreBuiltTaskDetail />
              ) : (
                <Unauthorized page="Task Library" />
              )
            }
          />
          <Route
            path="/task-library/my/:taskId"
            element={
              teamPageAccessAllowed(accessPermissions, 'ai') ? (
                <MyTaskDetail />
              ) : (
                <Unauthorized page="Task Library" />
              )
            }
          />
          <Route path="/custom" element={siteToolsGuard(<CustomReportsWithParams accessPermissions={accessPermissions} accessParams={accessParams} />)} />
          <Route path="/custom/:id" element={siteToolsGuard(<CustomReportsWithParams accessPermissions={accessPermissions} accessParams={accessParams} />)} />
          <Route
            path="/plan/*"
            element={
              teamPageAccessAllowed(accessPermissions, 'plan') ? (
                <Plan />
              ) : (
                <Unauthorized page="My Plan" />
              )
            }
          />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/*" element={<Profile />} />
          <Route
            path="/settings/*"
            element={<Settings {...accessParams} accessPermissions={accessPermissions} />}
          />
          <Route
            path="/faq/*"
            element={
              hasBigcommerce ? (
                <FAQBigcommerce />
              ) : (
                <FAQ loginFromChannel={loginFromChannel} />
              )
            }
          />
          <Route
            path="/shipping/*"
            element={siteToolsGuard(
              shippingFeatureEnabled &&
              teamShippingCapabilityAllowed(accessPermissions, SHIPPING_CAP_KEYS.VIEW) ? (
                <Shipping {...accessParams}/>
              ) : (
                <Unauthorized page="Shipping" />
              )
            )}
          />
          <Route
            path="/claims/*"
            element={siteToolsGuard(
              shippingFeatureEnabled &&
              teamShippingCapabilityAllowed(accessPermissions, SHIPPING_CAP_KEYS.VIEW) ? (
                <Claims />
              ) : (
                <Unauthorized page="Claims" />
              )
            )}
          />
          <Route path="/action/*" element={<Action />} />
          <Route path="/admin/*" element={<AdminRoute><StaffAdminRedirect /></AdminRoute>} />
          {/* Backward-compat redirects for old routes */}
          <Route path="/pipeline/*" element={<PipelineRedirect />} />
          <Route path="/staff/*" element={<Navigate to="/admin/users" replace />} />
          <Route path="/statistics/*" element={<Navigate to="/admin/operations" replace />} />
          <Route path="/backfills/*" element={<Navigate to="/admin/operations/jobs" replace />} />
          <Route
            path="/inventory/*"
            element={siteToolsGuard(
              teamPageAccessAllowed(accessPermissions, 'inventory') ? (
                <Inventory />
              ) : (
                <Unauthorized page="Inventory" />
              )
            )}
          />
          {/* Only send no-store users to /ai when they can actually access it;
              otherwise fall through to /dashboard, which renders SiteToolsLanding. */}
          <Route path="*" element={<Navigate to={defaultRedirect({ route: '/dashboard', hasAccounts, onboardSkipped, hasOrgWorkspace, requiresOnboarding, siteToolsOnly: showSiteTools && aiAllowed })} replace />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/action/*" element={<Action allowContinue={!subscriptionInactiveOrUpgradeRequired} />} />
          <Route path="/channels/*" element={<Channels />} />
          <Route path="/onboard/*" element={<SignupMultistep storeMarketplaceAccountCount={storeMarketplaceAccountCount} loginFromChannel={loginFromChannel} setOnboardSkipped={setOnboardSkipped}/>} />
          <Route
            path="/plan/*"
            element={
              teamPageAccessAllowed(accessPermissions, 'plan') ? (
                <Plan />
              ) : (
                <Unauthorized page="My Plan" />
              )
            }
          />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/*" element={<Profile />} />
          <Route
            path="/settings/*"
            element={<Settings {...accessParams} accessPermissions={accessPermissions} />}
          />
          <Route path="/faq/*" element={hasBigcommerce ? <FAQBigcommerce /> : <FAQ />} />
          <Route
            path="/shipping/*"
            element={
              shippingFeatureEnabled &&
              teamShippingCapabilityAllowed(accessPermissions, SHIPPING_CAP_KEYS.VIEW) ? (
                <Shipping {...accessParams}/>
              ) : (
                <Unauthorized page="Shipping" />
              )
            }
          />
          <Route
            path="/claims/*"
            element={
              shippingFeatureEnabled &&
              teamShippingCapabilityAllowed(accessPermissions, SHIPPING_CAP_KEYS.VIEW) ? (
                <Claims />
              ) : (
                <Unauthorized page="Claims" />
              )
            }
          />
          <Route path="/admin/*" element={<AdminRoute><StaffAdminRedirect /></AdminRoute>} />
          <Route path="/pipeline/*" element={<PipelineRedirect />} />
          <Route path="/staff/*" element={<Navigate to="/admin/users" replace />} />
          <Route path="/statistics/*" element={<Navigate to="/admin/operations" replace />} />
          <Route path="/backfills/*" element={<Navigate to="/admin/operations/jobs" replace />} />
          {/* No /ai route is mounted in this (setup-incomplete / inactive) branch, so
              never redirect to it here — that would loop. Force siteToolsOnly off. */}
          <Route path="*" element={<Navigate to={defaultRedirect({ route: (subscriptionInactiveOrUpgradeRequired && !admin) ? '/action' : '/onboard', hasAccounts, onboardSkipped, hasOrgWorkspace, requiresOnboarding, siteToolsOnly: false })} replace />} />
        </Routes>
      )
    }</Suspense>
  )

  const showActionModal =
    (limitedAccess || gatingBlocksRoutes) &&
    !(gatingBlocksRoutes ? freeAllowedPages : ['action', 'plan']).includes(gateApplicationId)
  // Only show the upgrade modal when the user explicitly triggers it (e.g. date
  // beyond lookback, export) — never auto-open on page load. Page-level route
  // gating (free user on a paid-only page) still shows the modal immediately.
  // Always trust the URL for dashboard: never auto-gate /dashboard even if Redux
  // applicationId drifted (common in the WordPress plugin iframe).
  const actionModalShow =
    (limitedAccess && upgradeModalOpen) ||
    (gatingBlocksRoutes && gateApplicationId !== 'dashboard')

  const actionModal = showActionModal && (
    <ActionModal
      show={actionModalShow}
      allowContinue={allowContinue && !isFreeGated && !isBusinessGated}
      continueTo={allowContinue ? '#' : ''}
      onClose={() => setUpgradeModalOpen(false)}
      isLite={isLite}
      subscriptionActive={subscriptionActive}
      isFreeUser={isFreeUser}
      isBusinessGated={isBusinessGated}
      backdropClassName={`plan-modal-backdrop backdrop-${['dashboard', 'traffic', 'ai', 'inventory', 'emailcampaigns', 'competitors', 'hotproducts'].includes(gateApplicationId) ? gateApplicationId : 'dashboard'}`}
    />
  )

  const toastAndTooltip = (
    <>
      <ToastContainer
        className="tw-text-sm"
        bodyClassName="tw-text-slate-800"
        autoClose={5000}
        hideProgressBar={true}
      />
      <ReactTooltip
        effect="float"
        type="dark"
        place="top"
        multiline={true}
      />
    </>
  )

  // Sidebar Layout
  return (
    <Router future={{ v7_relativeSplatPath: true }}>
      <div className="sidebar-layout tw-flex tw-flex-col tw-min-h-screen">
        {actionModal}
        <div className="tw-flex-1 tw-flex tw-min-h-0">
          <SidebarLayout
            hasAccounts={hasAccounts}
            isLite={isLite}
            accessPermissions={accessPermissions}
          >
            <MarketingBanner />
            <MfaSetupNudgeBanner />
            <ErrorBoundary suppress>
              {adminFetching ? null : (
                <IntercomCurl upgradeModalOpen={upgradeModalOpen} upgradeDismissed={upgradeDismissed} />
              )}
            </ErrorBoundary>
            {routesContent}
          </SidebarLayout>
        </div>
        <footer id="sub-analytics-footer" className="tw-flex-shrink-0 tw-border-t tw-border-sidebar-border tw-bg-white tw-h-[61px] tw-flex tw-items-center tw-justify-center tw-px-4">
          <Footer reportTimeCost={reportTimeCost} loadTimeMs={loadTimeMs} />
        </footer>
        {toastAndTooltip}
      </div>
    </Router>
  )
}

AppRouter.propTypes = {
  hasAccounts: bool,
}

export default AppRouter
